/**
 * What: Shared accepted/reused and truthful coverage contracts for asynchronous store sync.
 * Transport / data-flow: Validated at Shopify/Web routes and consumed by the merchant status UI.
 * No credentials, provider cursors, or raw customer data cross this boundary.
 */
import * as z from 'zod';
import { SHOPIFY_INGESTION_TARGET_DAYS } from './shopify-order-history-horizon.js';
const dateOrNull = z.string().datetime({ offset: true }).nullable();
export const storeSyncAcceptedSchema = z
    .object({
    status: z.enum(['accepted', 'reused']),
    connectionId: z.string().trim().min(1),
    statusUrl: z.literal('/api/store-sync/status'),
})
    .strict();
export const customerSyncResponseSchema = z
    .object({ kind: z.literal('customer_sync'), status: z.enum(['running', 'completed']) })
    .strict();
/**
 * Merchant sync-now acceptance for the per-store build path (#2971). Returned
 * only for connections already opted into builds; the legacy
 * `storeSyncAcceptedSchema` still covers connections that have no build yet.
 */
export const storeSyncBuildAcceptedSchema = z
    .object({
    kind: z.literal('build'),
    buildId: z.string().trim().min(1),
    status: z.enum(['started', 'already_running', 'fresh']),
})
    .strict();
/**
 * Merchant status projection for the per-store build path (#2971). The status
 * route returns this shape only when the connection has at least one
 * `store_builds` row; otherwise it returns `storeSyncStatusSchema`.
 */
export const storeSyncCoverageSchema = z
    .object({
    targetStart: z.string().datetime({ offset: true }),
    targetEnd: z.string().datetime({ offset: true }),
    collectedThrough: dateOrNull,
    coveredDays: z.number().int().nonnegative(),
    targetDays: z.literal(SHOPIFY_INGESTION_TARGET_DAYS),
    missingDays: z.array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)),
    /**
     * Collected days before the collectable window. Kept for records, never
     * fillable, never counted toward the target — the heat grid renders them
     * disabled so long-tenure stores see full history, not an arbitrary cut.
     */
    ancientDays: z.array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)),
    publishedStart: dateOrNull,
    publishedEnd: dateOrNull,
    /** Exact collectable days represented by the active Shopper Intelligence publication. */
    publishedDays: z
        .array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/))
        .max(90)
        .default([]),
    publicationId: z.string().trim().min(1).nullable(),
})
    .strict();
/** The build components shown to a merchant: settings plus the 10 kinds of Shopify data. */
export const STORE_SYNC_STAGE_KINDS = [
    'settings',
    'products',
    'customers',
    'orders',
    'inventory',
    'locations',
    'discounts',
    'price_rules',
    'content',
    'fulfillments',
];
/**
 * Honest per-kind progress for the latest build (#2971 Phase A, #3095, pillar 1 P1-16). Each kind
 * loads on its own: `state` is the latest build's progress for that kind, including `failed` (with
 * `errorCode`) and `scope_denied`; `usable` says whether an earlier good copy of this kind can
 * already be used while it refreshes. Shopify's running count rides along as `objectCount`; it is
 * null until the first poll and always null for `settings` (a single Admin fetch).
 */
export const storeSyncBuildStageSchema = z
    .object({
    kind: z.enum(STORE_SYNC_STAGE_KINDS),
    state: z.enum(['queued', 'exporting', 'downloading', 'loading', 'completed', 'failed', 'scope_denied']),
    objectCount: z.number().int().nonnegative().nullable(),
    loadedRows: z.number().int().nonnegative().nullable(),
    errorCode: z.string().nullable().default(null),
    usable: z.boolean().default(false),
})
    .strict();
/**
 * The shop's own synced settings, for the merchant Store Data display (#3095). Mirrors the
 * `ShopSettings` a build persists to `shopify_shop_settings`. Served by
 * `/api/store-sync/shop-settings` and is `null` until a build has synced them — a missing row is
 * "not synced yet", never a guessed default currency or store name.
 */
export const shopSettingsSchema = z
    .object({
    currencyCode: z.string().trim().min(1),
    shopName: z.string().trim().min(1),
    ianaTimezone: z.string().trim().min(1),
    primaryDomainHost: z.string().trim().min(1),
})
    .strict();
export const storeSyncBuildStatusSchema = z
    .object({
    stage: z.literal('build'),
    active: z
        .object({
        buildId: z.string().trim().min(1),
        cutoff: z.string().trim().min(1),
        publishedAt: z.string().nullable(),
    })
        .strict()
        .nullable(),
    latest: z
        .object({
        buildId: z.string().trim().min(1),
        status: z.enum(['running', 'published', 'failed', 'superseded']),
        errorCode: z.string().nullable(),
        startedAt: z.string().trim().min(1),
        publishedAt: z.string().nullable(),
        /** Settings-first per-kind progress; always exactly one entry per `STORE_SYNC_STAGE_KINDS`. */
        stages: z.array(storeSyncBuildStageSchema).length(STORE_SYNC_STAGE_KINDS.length),
        /** When the connection's products first became usable (the products latest good load). */
        catalogReadyAt: z.string().nullable(),
    })
        .strict()
        .nullable(),
    /** Day coverage of the active build's order window, for the collection-history panel. */
    coverage: storeSyncCoverageSchema.nullable().default(null),
})
    .strict();
export const storeSyncStatusSchema = z
    .object({
    /**
     * Merchant-visible sync phase. Each value has exactly one meaning; no two
     * conditions share a status (see docs/concepts/ingestion/sync-status.md):
     * idle = no units planned; queued = requested, zero durable output;
     * running = live units, zero accepted; yielded = live units + partial
     * accepted; parked_partial = partial accepted, zero live units, nothing
     * scheduled; retrying = zero accepted, zero live, a run existed;
     * completed = full window accepted; failed = legacy/emitted-with-error only.
     * Render activity from `syncActive`, never by interpreting this enum.
     */
    status: z.enum(['idle', 'queued', 'running', 'yielded', 'parked_partial', 'retrying', 'completed', 'failed']),
    stage: z.string().trim().min(1).nullable(),
    coverage: storeSyncCoverageSchema.nullable(),
    activeUnits: z.number().int().nonnegative(),
    retryAt: dateOrNull,
    publicationPending: z.boolean(),
    errorMessage: z.string().nullable(),
    /**
     * Backend-owned activity truth: true only while units hold a live run.
     * Consumers must render sync state from this field, never by interpreting
     * `status` — parked `yielded` coverage reports `syncActive: false`.
     */
    syncActive: z.boolean(),
    /**
     * Per-stage build progress (#2971 Phase A), populated only for connections on the per-store
     * build path. `null` for every legacy-shaped status and once a build's stages are no longer
     * meaningful to show (e.g. idle). Optional so existing callers that never set it keep parsing.
     */
    buildProgress: z
        .object({
        stages: z.array(storeSyncBuildStageSchema).length(STORE_SYNC_STAGE_KINDS.length),
        catalogReadyAt: z.string().nullable(),
    })
        .strict()
        .nullable()
        .default(null),
})
    .strict();
//# sourceMappingURL=store-sync.js.map